import React, { useEffect, useState } from 'react';
import '../stylings/BucksTermsOfService.css';

function BucksTermsOfService() {
  const [isVisible, setIsVisible] = useState(false);
  const currentYear = new Date().getFullYear();
  
  // Animation on scroll effect
  useEffect(() => {
    setIsVisible(true);
    
    // Smooth scroll to section if hash is present in URL
    const hash = window.location.hash;
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
    
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bucks-terms-of-service">
      {/* Hero Section */}
      <section className={`terms-hero ${isVisible ? 'visible' : ''}`}>
        <div className="terms-hero-content">
          <h1>Terms of Service</h1>
          <p className="terms-subtitle">
            Please read these terms carefully before using Buck's Farmstand services.
          </p>
          <div className="terms-decoration"></div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="terms-toc-section">
        <div className="terms-container">
          <div className="terms-toc">
            <h2>Contents</h2>
            <ul>
              <li><a href="#overview">Overview</a></li>
              <li><a href="#site-usage">Site Usage</a></li>
              <li><a href="#accounts">Account Terms</a></li>
              <li><a href="#products">Products & Purchases</a></li>
              <li><a href="#payments">Payment Terms</a></li>
              <li><a href="#shipping">Shipping & Delivery</a></li>
              <li><a href="#returns">Returns & Refunds</a></li>
              <li><a href="#intellectual-property">Intellectual Property</a></li>
              <li><a href="#third-party">Third-Party Services</a></li>
              <li><a href="#liability">Limitation of Liability</a></li>
              <li><a href="#disputes">Dispute Resolution</a></li>
              <li><a href="#changes">Changes To Terms</a></li>
              <li><a href="#contact">Contact Us</a></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="terms-content-section">
        <div className="terms-container">
          
          {/* Overview */}
          <div id="overview" className="terms-section">
            <h2>Overview</h2>
            <p>
              These Terms of Service ("Terms") govern your use of the Buck's Farmstand website, services, and products. By accessing our website, creating an account, or making a purchase, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you may not use our services.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Buck's Farmstand. These Terms were last updated on May 6, 2025.
            </p>
          </div>
          
          {/* Site Usage */}
          <div id="site-usage" className="terms-section">
            <h2>Site Usage</h2>
            <p>
              Buck's Farmstand grants you a limited, non-exclusive, non-transferable, and revocable license to access and use our website for personal, non-commercial purposes. You agree not to:
            </p>
            <ul className="terms-list">
              <li>Use our website in any way that violates applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the website</li>
              <li>Use the website to transmit malware, viruses, or other harmful code</li>
              <li>Interfere with the proper functioning of the website</li>
              <li>Scrape, data mine, or otherwise extract data from our website using automated means</li>
              <li>Use our website for any purpose other than its intended purpose</li>
            </ul>
            <p>
              We reserve the right to restrict or terminate your access to our website at any time, without notice, for any reason, including if we believe you have violated these Terms.
            </p>
          </div>
          
          {/* Products & Purchases */}
          <div id="products" className="terms-section">
            <h2>Products & Purchases</h2>
            <p>
              Buck's Farmstand offers fresh, locally grown produce and related products. By placing an order, you understand that:
            </p>
            <ul className="terms-list">
              <li>Product availability is subject to seasonal changes and availability</li>
              <li>Images of products are for illustrative purposes and actual products may vary</li>
              <li>We reserve the right to limit quantities or refuse orders</li>
              <li>Prices are subject to change without notice</li>
              <li>Orders are subject to acceptance by Buck's Farmstand</li>
              <li>Fresh produce is a natural product and may vary in appearance, size, or weight</li>
            </ul>
            <p>
              If a product is unavailable after you place an order, we will notify you and may offer a suitable replacement or refund.
            </p>
          </div>
          
          {/* Payment Terms */}
          <div id="payments" className="terms-section">
            <h2>Payment Terms</h2>
            <p>
              All payments are processed securely through Square, our third-party payment processor. By making a purchase, you agree to:
            </p>
            <ul className="terms-list">
              <li>Pay all charges at the prices listed at the time of purchase</li>
              <li>Pay all applicable taxes</li>
              <li>Use a valid payment method that you are legally authorized to use</li>
              <li>Ensure that your payment information is accurate and up-to-date</li>
            </ul>
            <div className="terms-notice">
              <strong>Note:</strong> Buck's Farmstand does not store your credit card information. All payment processing is handled securely by Square, in accordance with their Terms of Service and Privacy Policy.
            </div>
          </div>
          
          {/* Shipping & Delivery */}
          <div id="shipping" className="terms-section">
            <h2>Shipping & Delivery</h2>
            <p>
              For delivery orders, you understand and agree that:
            </p>
            <ul className="terms-list">
              <li>Delivery is available only within our designated service area</li>
              <li>Delivery times are estimates and not guaranteed</li>
              <li>You must provide accurate delivery information</li>
              <li>Someone must be available to receive deliveries of perishable items</li>
              <li>Delivery fees may apply and will be clearly stated at checkout</li>
              <li>We are not responsible for delays caused by factors outside our control</li>
            </ul>
            <p>
              For our Buck's Box subscription service, specific delivery terms will be provided during the subscription process.
            </p>
          </div>
          
          {/* Returns & Refunds */}
          <div id="returns" className="terms-section">
            <h2>Returns & Refunds</h2>
            <p>
              We take pride in the quality of our products. If you are not satisfied with your purchase:
            </p>
            <ul className="terms-list">
              <li>You must notify us within 24 hours of receiving your order</li>
              <li>For quality issues, please provide photos of the affected products</li>
              <li>Refunds or replacements are provided at our discretion</li>
              <li>Perishable items cannot be returned but may be eligible for refund</li>
              <li>Non-perishable items in unopened, original condition may be returned within 7 days</li>
            </ul>
            <p>
              To initiate a return or request a refund, please contact us at returns@bucksfarmstand.com or call us at (708) 555-1234.
            </p>
          </div>
          
          {/* Intellectual Property */}
          <div id="intellectual-property" className="terms-section">
            <h2>Intellectual Property</h2>
            <p>
              All content on our website, including but not limited to text, graphics, logos, images, audio clips, digital downloads, and software, is the property of Buck's Farmstand or its content suppliers and is protected by United States and international copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              You may not reproduce, distribute, modify, display, perform, or use any materials from our website without our prior written consent. The Buck's Farmstand name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of Buck's Farmstand. You may not use these marks without our prior written consent.
            </p>
          </div>
          
          {/* Third-Party Services */}
          <div id="third-party" className="terms-section">
            <h2>Third-Party Services</h2>
            <p>
              Our website may integrate with third-party services. Your use of these services is subject to the respective terms and privacy policies of these third parties.
            </p>
            <p>
              Buck's Farmstand uses Square for payment processing. By making a purchase, you also agree to Square's Terms of Service and Privacy Policy. We encourage you to review these documents to understand how Square processes your payment information.
            </p>
          </div>
          
          {/* Limitation of Liability */}
          <div id="liability" className="terms-section">
            <h2>Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Buck's Farmstand shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="terms-list">
              <li>Your use or inability to use our website or services</li>
              <li>Any unauthorized access to or use of our servers or personal information</li>
              <li>Any interruption or cessation of transmission to or from our website</li>
              <li>Any bugs, viruses, or other harmful code that may be transmitted through our website</li>
              <li>Any errors or omissions in any content or for any loss or damage of any kind incurred as a result of your use of any content</li>
            </ul>
            <p>
              In no event shall our total liability to you for all claims exceed the amount paid by you to Buck's Farmstand in the past six months.
            </p>
          </div>
          
          {/* Dispute Resolution */}
          <div id="disputes" className="terms-section">
            <h2>Dispute Resolution</h2>
            <p>
              In the event of any dispute arising out of or relating to these Terms, you agree to first attempt to resolve the dispute informally by contacting us. If we are unable to resolve the dispute informally, any dispute will be resolved through binding arbitration in Cook County, Illinois, except that you may assert claims in small claims court if your claims qualify.
            </p>
            <p>
              You agree to bring any claims within one year after the claim arises, or such longer period as required by applicable law. You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
            </p>
          </div>
          
          {/* Changes to Terms */}
          <div id="changes" className="terms-section">
            <h2>Changes To Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of any changes by posting the new Terms on this page and updating the "last updated" date. You are advised to review these Terms periodically for any changes. Changes to these Terms are effective when they are posted on this page.
            </p>
            <p>
              Your continued use of our website after we post changes to these Terms constitutes your acceptance of those changes.
            </p>
          </div>
          
          {/* Contact Us */}
          <div id="contact" className="terms-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding these Terms of Service, please contact us at:
            </p>
            <div className="terms-contact-info">
              <p><strong>Buck's Farmstand</strong></p>
              <p>4250 W 123rd St</p>
              <p>Alsip, IL 60803</p>
              <p>Email: terms@bucksfarmstand.com</p>
              <p>Phone: (708) 555-1234</p>
            </div>
          </div>
          
          <div className="terms-footer">
            <p>&copy; {currentYear} Buck's Farmstand. All Rights Reserved.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BucksTermsOfService;